import Highlight from "@/components/home/Highlight";
import ContactLink from "@/components/ContactLink";
import { Head, Link, usePage } from "@inertiajs/react";
import {
  ArrowRight, BookOpen, Heart, MapPin, Upload, QrCode, Clock,
  DollarSign, TrendingUp, Users, Trophy, Medal, CheckCircle2,
  BarChart3, Download, Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import PageContainer from "@/components/marketing/PageContainer";
import SectionHeading from "@/components/marketing/SectionHeading";
import certificatePreview from "@/images/certificate-preview.jpg";

export default function HowItWorks() {
  const { routes } = usePage().props;

  return (
    <MarketingLayout>
      <Head title="How it works" />

      {/* ── Hero ────────────────────────────────────────────── */}
      <PageContainer width="narrow" className="text-center space-y-6 pt-10 md:pt-14">
        <SectionHeading
          as="h1"
          flourish="how it works"
          title={<>From setup to fundraiser in <Highlight color="amber">one afternoon.</Highlight></>}
        />
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          PagePledge helps you run your read-a-thon, from importing classrooms to collecting donations. Here’s what each person sees.
        </p>
      </PageContainer>

      {/* ══════════════════════════════════════════════════════
          SECTION 1: School Admin
          ══════════════════════════════════════════════════════ */}
      <PageContainer width="wide" className="py-10 md:py-12" outerClassName="bg-primary/10 border-t border-primary/15">
        <div className="max-w-5xl mx-auto space-y-10">
          <RoleHeader
            badge="School admin"
            badgeColor="primary"
            title="Set up and manage the read-a-thon"
            description="Principals and school admins use one dashboard to set up the campaign, import their roster, print take-home sheets, and track donations."
          />

          <div className="grid gap-8 md:grid-cols-2">
            <StepCard
              number="1"
              title="Create a campaign"
              description="Name it, set the dates and goals, and connect your school’s Square or Stripe account to receive donations."
              preview={
                <MockUI>
                  <div className="space-y-3">
                    <div className="h-3 w-48 bg-foreground/10 rounded" />
                    <div className="grid grid-cols-2 gap-2">
                      <MockInput label="Start date" value="Apr 3, 2026" />
                      <MockInput label="End date" value="Apr 17, 2026" />
                    </div>
                    <MockInput label="Fundraising goal" value="$5,000" />
                    <div className="h-9 w-full bg-primary rounded-lg flex items-center justify-center">
                      <span className="text-xs text-primary-foreground font-medium">Create campaign</span>
                    </div>
                  </div>
                </MockUI>
              }
            />
            <StepCard
              number="2"
              title="Import the classrooms"
              description="Upload a CSV with student names and classrooms. Activation codes and QR-coded take-home sheets are generated automatically."
              preview={
                <MockUI>
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground"><Upload className="h-3.5 w-3.5" /> classrooms.csv uploaded</div>
                    <MockTableRow name="Room 12" detail="Grade 5 · 7 students" />
                    <MockTableRow name="Room 14" detail="Grade 5 · 7 students" />
                    <MockTableRow name="Room 08" detail="Grade 4 · 6 students" />
                  </div>
                </MockUI>
              }
            />
            <StepCard
              number="3"
              title="Print take-home sheets"
              description="Each student gets a page with their name, an activation code, and a QR code. Parents scan it to follow their student's reading."
              preview={
                <MockUI>
                  <div className="space-y-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Take-home sheet</div>
                    <div className="font-display text-lg font-semibold">Sarah Thompson</div>
                    <div className="mx-auto w-16 h-16 border-2 border-foreground/20 rounded-lg flex items-center justify-center">
                      <QrCode className="h-10 w-10 text-foreground/30" />
                    </div>
                    <div className="font-mono text-sm tracking-wider text-primary font-semibold">BEAR-MOON-4217</div>
                  </div>
                </MockUI>
              }
            />
            <StepCard
              number="4"
              title="Watch the dashboard"
              description="Live stats, leaderboards, rewards, and a transaction log. See every donation in real time."
              preview={
                <MockUI>
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      <MockStat label="Raised" value="$915" color="primary" />
                      <MockStat label="Minutes" value="2,505" color="accent" />
                      <MockStat label="Students" value="20" color="info" />
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full w-[18%] bg-primary rounded-full" />
                    </div>
                    <div className="text-[10px] text-muted-foreground">18% of $5,000 goal</div>
                  </div>
                </MockUI>
              }
            />
          </div>
        </div>
      </PageContainer>

      {/* ══════════════════════════════════════════════════════
          SECTION 2: Parents
          ══════════════════════════════════════════════════════ */}
      <PageContainer width="wide" className="py-10 md:py-12" outerClassName="bg-accent/10 border-t border-accent/15">
        <div className="max-w-5xl mx-auto space-y-10">
          <RoleHeader
            badge="Parents"
            badgeColor="accent"
            title="Follow along and log reading at home"
            description="Parents scan the QR code on the take-home sheet, activate their child, and can log reading minutes and share the donation page with family."
          />

          <div className="grid gap-8 md:grid-cols-3">
            <StepCard
              number="1"
              title="Scan the QR code"
              description="The take-home sheet has a QR code that links straight to the activation page. Enter your email and you're in."
              small
              preview={
                <MockUI compact>
                  <div className="space-y-2 text-center">
                    <div className="font-display text-sm font-semibold">Follow your child's reading</div>
                    <MockInput label="Activation code" value="BEAR-MOON-4217" />
                    <MockInput label="Your email" value="" />
                    <div className="h-8 bg-primary rounded-lg flex items-center justify-center">
                      <span className="text-[10px] text-primary-foreground font-medium">Email me my link</span>
                    </div>
                  </div>
                </MockUI>
              }
            />
            <StepCard
              number="2"
              title="Track progress"
              description="See how much your child has raised, how many minutes they've read, and where they rank in their class."
              small
              preview={
                <MockUI compact>
                  <div className="space-y-2">
                    <div className="font-display text-sm font-semibold">Sarah Thompson</div>
                    <div className="grid grid-cols-2 gap-2">
                      <MockStat label="Raised" value="$75" color="primary" small />
                      <MockStat label="Minutes" value="135" color="accent" small />
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px]">
                      <span>🏆</span>
                      <span className="font-medium text-foreground">Leading Room 12!</span>
                    </div>
                  </div>
                </MockUI>
              }
            />
            <StepCard
              number="3"
              title="Log reading minutes"
              description="After bedtime stories, log the minutes right from your phone. Teachers can also log classroom reading time."
              small
              preview={
                <MockUI compact>
                  <div className="space-y-2">
                    <div className="text-xs font-medium">How long did Sarah read?</div>
                    <MockInput label="Minutes" value="20" />
                    <MockInput label="Note" value="Charlotte's Web · ch 3" />
                    <div className="h-7 bg-primary rounded-lg flex items-center justify-center">
                      <span className="text-[10px] text-primary-foreground font-medium">Log 20 min</span>
                    </div>
                  </div>
                </MockUI>
              }
            />
          </div>
        </div>
      </PageContainer>

      {/* ══════════════════════════════════════════════════════
          SECTION 3: Teachers
          ══════════════════════════════════════════════════════ */}
      <PageContainer width="wide" className="py-10 md:py-12" outerClassName="bg-info/10 border-t border-info/15">
        <div className="max-w-5xl mx-auto space-y-10">
          <RoleHeader
            badge="Teachers"
            badgeColor="info"
            title="Log in-class minutes and cheer your students on"
            description="Teachers sign in with a magic link to log reading time for the whole class at once and watch their classroom climb the leaderboard."
          />

          <div className="grid gap-8 md:grid-cols-3">
            <StepCard
              number="1"
              title="Sign in with your email"
              description="No password to remember. Enter your school email and click the magic link we send you."
              small
              preview={
                <MockUI compact>
                  <div className="space-y-2 text-center">
                    <div className="font-display text-sm font-semibold">Sign in to PagePledge</div>
                    <MockInput label="Email" value="ms.lee@lincoln.ca" />
                    <div className="h-8 bg-primary rounded-lg flex items-center justify-center">
                      <span className="text-[10px] text-primary-foreground font-medium">Email me a link</span>
                    </div>
                  </div>
                </MockUI>
              }
            />
            <StepCard
              number="2"
              title="Log in-class minutes"
              description="One screen, whole class. Pre-checked students, uncheck anyone absent, add a note, log it in seconds."
              small
              preview={
                <MockUI compact>
                  <div className="space-y-2">
                    <div className="text-xs font-medium">Room 12 read for…</div>
                    <MockInput label="Minutes" value="20" />
                    <div className="space-y-1">
                      <MockCheckRow name="Sarah T." checked />
                      <MockCheckRow name="Marcus J." checked />
                      <MockCheckRow name="Priya K." />
                    </div>
                    <div className="h-7 bg-primary rounded-lg flex items-center justify-center">
                      <span className="text-[10px] text-primary-foreground font-medium">Log for 2 students</span>
                    </div>
                  </div>
                </MockUI>
              }
            />
            <StepCard
              number="3"
              title="See your class climb"
              description="See classroom stats, the student leaderboard, and school-wide progress on one page."
              small
              preview={
                <MockUI compact>
                  <div className="space-y-2">
                    <div className="font-display text-sm font-semibold">Room 12</div>
                    <div className="grid grid-cols-2 gap-2">
                      <MockStat label="Raised" value="$310" color="primary" small />
                      <MockStat label="Minutes" value="845" color="accent" small />
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px]">
                      <Trophy className="h-3 w-3 text-warning" />
                      <span className="font-medium text-foreground">#1 in the school</span>
                    </div>
                  </div>
                </MockUI>
              }
            />
          </div>
        </div>
      </PageContainer>

      {/* ══════════════════════════════════════════════════════
          SECTION 4: Donors
          ══════════════════════════════════════════════════════ */}
      <PageContainer width="wide" className="py-10 md:py-12" outerClassName="bg-warning/10 border-t border-warning/15 border-b">
        <div className="max-w-5xl mx-auto space-y-10">
          <RoleHeader
            badge="Donors"
            badgeColor="warning"
            title="Grandparents, family, and friends donate in 30 seconds"
            description="Parents share a link so donors can see the student’s progress and the school’s rewards. Donors can pay by card without creating an account."
          />

          <div className="grid gap-8 md:grid-cols-2">
            <StepCard
              number="1"
              title="See the student's page"
              description="A clean donation page showing who they're raising for, how the school is doing, and what rewards unlock as they hit each goal."
              preview={
                <MockUI>
                  <div className="space-y-3">
                    <div className="font-display text-sm font-semibold">Sarah Thompson is raising money for Lincoln Elementary</div>
                    <div className="h-2.5 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full w-[20%] bg-primary rounded-full" />
                    </div>
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span className="font-semibold text-primary">$1,015</span>
                      <span>of $5,000 goal</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px]">
                      <CheckCircle2 className="h-3 w-3 text-success" />
                      <span>Pizza party: <span className="text-success font-medium">Unlocked!</span></span>
                    </div>
                  </div>
                </MockUI>
              }
            />
            <StepCard
              number="2"
              title="Donate with a card"
              description="Choose an amount and pay by card through Square or Stripe. Donors can cover the provider’s processing fees. PagePledge adds no transaction fees."
              preview={
                <MockUI>
                  <div className="space-y-3">
                    <div className="text-xs font-medium">Support Sarah</div>
                    <div className="grid grid-cols-4 gap-1.5">
                      {["$5", "$10", "$25", "$50"].map((a) => (
                        <div key={a} className={`h-7 rounded border flex items-center justify-center text-[10px] font-medium ${a === "$25" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{a}</div>
                      ))}
                    </div>
                    <div className="h-8 rounded-lg border border-border bg-card flex items-center px-2 text-[10px] text-muted-foreground/40 italic">Card number</div>
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                      <div className="h-3 w-3 rounded border border-primary bg-primary/20" />
                      Cover the $0.77 fee so the school gets the full $25
                    </div>
                    <div className="h-8 bg-primary rounded-lg flex items-center justify-center">
                      <span className="text-[10px] text-primary-foreground font-medium">Donate $25.77</span>
                    </div>
                  </div>
                </MockUI>
              }
            />
          </div>
        </div>
      </PageContainer>

      <PageContainer width="wide" id="prizes-and-certificates" className="py-10 md:py-12" outerClassName="bg-accent/5 border-b border-accent/15">
        <div className="max-w-5xl mx-auto space-y-10">
          <RoleHeader
            badge="Prizes & certificates"
            badgeColor="accent"
            title="Ready to hand out the prizes?"
            description="See who has earned each prize, mark it handed out, and print certificates for your students."
          />
          <div className="grid gap-8 md:grid-cols-2">
            <StepCard
              number="1"
              title="Know who still needs their prize"
              description="Set prizes for reading or fundraising milestones. Students appear in the prize tracker as they earn them. Teachers mark prizes handed out, and school admins can check what’s still waiting across classrooms."
              preview={
                <MockUI>
                  <div className="space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 text-sm font-semibold"><BookOpen className="h-4 w-4 text-accent" /> Choose a book</div>
                        <p className="mt-1 text-xs text-muted-foreground">Earned after reading 500 minutes</p>
                      </div>
                      <div className="text-right shrink-0"><div className="font-display text-xl">1 / 3</div><div className="text-[10px] text-muted-foreground">handed out</div></div>
                    </div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full w-1/3 bg-accent" /></div>
                    <div className="divide-y divide-border">
                      {[{ name: "Sarah T.", done: true }, { name: "Marcus J.", done: false }, { name: "Priya K.", done: false }].map(({ name, done }) => (
                        <div key={name} className="flex flex-wrap items-center justify-between gap-2 py-3 text-xs">
                          <span className="font-medium">{name}</span>
                          <span className={done ? "flex items-center gap-1 text-success" : "rounded-md bg-primary px-2 py-1 text-primary-foreground"}>
                            {done && <CheckCircle2 className="h-3 w-3" />}
                            {done ? "Handed out" : "Mark handed out"}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-muted-foreground">Sample classroom prize tracker</p>
                  </div>
                </MockUI>
              }
            />
            <StepCard
              number="2"
              title="Print a certificate for every reader"
              description="Download personalised certificates for one student, a classroom, or the whole school. Certificates celebrate participation and recognise top readers and fundraisers with award badges."
              preview={
                <figure className="space-y-3">
                  <a href={certificatePreview} target="_blank" rel="noopener noreferrer" className="block rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary" aria-label="View full-size sample certificate in a new tab">
                    <img src={certificatePreview} alt="Sample PagePledge certificate of achievement for Sarah Thompson, with Gold Reader and Top 10 Reader awards" width="1584" height="1224" loading="lazy" className="w-full h-auto rounded-xl border border-border shadow-sm" />
                  </a>
                  <figcaption className="text-xs text-muted-foreground text-center">
                    Sample certificate with fictional details. Select it to view full size.
                  </figcaption>
                </figure>
              }
            />
          </div>
        </div>
      </PageContainer>

      {/* ── Values ──────────────────────────────────────────── */}
      <PageContainer width="wide" className="pt-8 md:pt-10">
        <div className="grid gap-8 md:grid-cols-3 max-w-4xl mx-auto">
          <ValueCard icon={<Heart className="h-6 w-6 text-primary" />} title="Schools keep what they raise" description="No percentage fees. A flat cost per campaign, known upfront. Donors can cover card processing so the school gets 100%." />
          <ValueCard icon={<BookOpen className="h-6 w-6 text-accent" />} title="Reading comes first" description="The whole point is getting students to read. We keep the fundraising mechanics out of the way so the focus stays on the books." />
          <ValueCard icon={<Shield className="h-6 w-6 text-info" />} title="Built in Canada 🇨🇦" description="Canadian hosting, Canadian payment processing, Canadian privacy law." />
        </div>
      </PageContainer>

      {/* ── CTA ─────────────────────────────────────────────── */}
      <PageContainer width="narrow" className="text-center space-y-6 pb-12 md:pb-16">
        <SectionHeading flourish="interested?" title="We'd love to hear from you." subhead="Early adopter discounts are available now. If your school runs a read-a-thon, we'd love to help you get started." />
        <div className="flex flex-wrap gap-3 justify-center">
          <Button size="lg" asChild><Link href={routes.new_school_application}>Sign up your school <ArrowRight className="h-4 w-4" /></Link></Button>
          <Button size="lg" variant="outline" asChild><ContactLink>Email us</ContactLink></Button>
        </div>
      </PageContainer>
    </MarketingLayout>
  );
}

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────

function RoleHeader({ badge, badgeColor, title, description }) {
  const colors = { primary: "bg-primary/10 text-primary", accent: "bg-accent/10 text-accent", warning: "bg-warning/10 text-warning-foreground", info: "bg-info/10 text-info" }[badgeColor] || "bg-secondary text-foreground";
  return (
    <div className="text-center space-y-3">
      <Badge className={`${colors} text-xs uppercase tracking-wider px-3 py-1`}>{badge}</Badge>
      <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">{title}</h2>
      <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">{description}</p>
    </div>
  );
}

function StepCard({ number, title, description, preview, small = false }) {
  return (
    <div className="row-span-2 grid grid-rows-subgrid gap-4">
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">{number}</span>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed mt-1">{description}</p>
        </div>
      </div>
      <div className="self-start">{preview}</div>
    </div>
  );
}

function ValueCard({ icon, title, description }) {
  return (
    <div className="space-y-3 text-center">
      <div className="flex justify-center">{icon}</div>
      <h3 className="font-medium text-foreground">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

// ── Mock UI primitives (non-interactive illustrations) ────────

function MockUI({ children, compact = false }) {
  return (
    <div className={`rounded-xl border border-border bg-card shadow-sm ${compact ? "p-3" : "p-4"} select-none pointer-events-none`}>
      {children}
    </div>
  );
}

function MockInput({ label, value }) {
  return (
    <div className="space-y-1">
      <div className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="h-7 rounded-md border border-input bg-card px-2 flex items-center text-[11px] text-foreground/70 italic">{value || <span className="text-muted-foreground/40">...</span>}</div>
    </div>
  );
}

function MockTableRow({ name, detail }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
      <div>
        <div className="text-xs font-medium text-foreground">{name}</div>
        <div className="text-[10px] text-muted-foreground">{detail}</div>
      </div>
      <ArrowRight className="h-3 w-3 text-muted-foreground/40" />
    </div>
  );
}

function MockCheckRow({ name, checked = false }) {
  return (
    <div className="flex items-center gap-2 text-[10px]">
      <div className={`h-3 w-3 rounded border ${checked ? "border-primary bg-primary/20" : "border-input bg-card"}`} />
      <span className={checked ? "text-foreground" : "text-muted-foreground/60 line-through"}>{name}</span>
    </div>
  );
}

function MockStat({ label, value, color, small = false }) {
  const colors = { primary: "bg-primary/10 text-primary", accent: "bg-accent/10 text-accent", info: "bg-info/10 text-info" }[color] || "bg-secondary text-foreground";
  return (
    <div className={`rounded-lg ${colors} ${small ? "px-2 py-1.5" : "px-3 py-2"} text-center`}>
      <div className={`font-display font-semibold ${small ? "text-sm" : "text-base"}`}>{value}</div>
      <div className="text-[8px] uppercase tracking-wider opacity-70">{label}</div>
    </div>
  );
}
